import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import {
  supabase,
  signOut, getMembers, addMember, updateMember, deleteMember,
  getSessions, addSession, updateSession, deleteSession,
  getLocations, addLocation, updateLocation, deleteLocation,
  getAttendance, overrideAttendance, getInvites, inviteLecturer,
  revokeInvite, assignLecturerToSession,
  getPlanPricing,
} from '../lib/supabase'
import { exportAttendance, exportMembers } from '../lib/export'
import { DAYS, GPS_WARNING, PLANS, PLAN_FEATURES } from '../lib/config'
import MemberUpload from '../components/MemberUpload'

// ── Simple inline bar chart ───────────────────────────────────────
function BarChart({ data, color = 'var(--blue)', height = 140 }) {
  const max = Math.max(...data.map(d => d.value), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height, padding: '0 4px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>{d.value > 0 ? d.value : ''}</div>
          <div style={{ width: '100%', background: color, borderRadius: '4px 4px 0 0', height: `${Math.max((d.value / max) * 85, d.value > 0 ? 4 : 0)}%`, transition: 'height 0.4s ease', opacity: 0.85 }} title={`${d.label}: ${d.value}`} />
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', maxWidth: 40, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</div>
        </div>
      ))}
    </div>
  )
}

export default function Admin() {
  const { user, org, loading, refreshOrg } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState('dashboard')
  const [members, setMembers] = useState([])
  const [sessions, setSessions] = useState([])
  const [locations, setLocations] = useState([])
  const [attendance, setAttendance] = useState([])
  const [invites, setInvites] = useState([])
  const [dataLoading, setDataLoading] = useState(false)
  const [liveCount, setLiveCount] = useState(0)

  // Modals
  const [memberModal, setMemberModal] = useState(null)
  const [sessionModal, setSessionModal] = useState(null)
  const [locationModal, setLocationModal] = useState(null)
  const [overrideModal, setOverrideModal] = useState(null)
  const [inviteShareModal, setInviteShareModal] = useState(null)
  const [upgradeModal, setUpgradeModal] = useState(false)

  // Settings state
  const [settingsForm, setSettingsForm] = useState({ portal_banner: '', announce_text: '', announce_active: false, vanity_slug: '', admin_display_name: '' })
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [settingsSaved, setSettingsSaved] = useState(false)
  const [settingsError, setSettingsError] = useState('')

  // Export filters
  const [exportFilters, setExportFilters] = useState({ session: '', dateFrom: '', dateTo: '', format: 'xlsx' })

  const features = PLAN_FEATURES[org?.plan] || PLAN_FEATURES.starter

  const L = org ? {
    member: org.member_label || 'Member',
    members: org.members_label || 'Members',
    idField: org.id_field_label || 'ID',
    session: org.session_label || 'Session',
    sessions: org.sessions_label || 'Sessions',
    group: org.group_label || 'Group',
    location: org.location_label || 'Location',
    locations: org.locations_label || 'Locations',
    attendance: org.attendance_label || 'Attendance',
  } : {}

  const loadAll = useCallback(async () => {
    if (!org) { setDataLoading(false); return }
    setDataLoading(true)
    const [m, s, l, a, inv] = await Promise.all([
      getMembers(org.id),
      getSessions(org.id),
      getLocations(org.id),
      getAttendance(org.id, { limit: 500 }),
      getInvites(org.id),
    ])
    setMembers(m.data || [])
    setSessions(s.data || [])
    setLocations(l.data || [])
    setAttendance(a.data || [])
    setInvites(inv.data || [])
    setDataLoading(false)
  }, [org])

  useEffect(() => {
    if (!loading && !user) navigate('/login')
  }, [loading, user, navigate])

  useEffect(() => {
    if (org) {
      loadAll()
      setSettingsForm({
        portal_banner: org.portal_banner || '',
        announce_text: org.announce_text || '',
        announce_active: org.announce_active || false,
        vanity_slug: org.vanity_slug || '',
        admin_display_name: org.admin_display_name || '',
      })
    }
  }, [org, loadAll])

  // ── Realtime live counter ─────────────────────────────────────
  useEffect(() => {
    if (!org) return
    const today = new Date().toISOString().slice(0, 10)
    const channel = supabase
      .channel('live-attendance-' + org.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'attendance', filter: `org_id=eq.${org.id}` }, (payload) => {
        if (payload.new.status === 'present' && payload.new.marked_at?.startsWith(today)) {
          setLiveCount(c => c + 1)
          setAttendance(prev => [payload.new, ...prev])
        }
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [org])

  if (loading || dataLoading) return (
    <div className="page-center"><span className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} /></div>
  )
  if (!org) return (
    <div className="page-center">
      <div className="card">
        <p>No organisation found. <span style={{ color: 'var(--blue)', cursor: 'pointer' }} onClick={() => navigate('/signup')}>Complete setup →</span></p>
      </div>
    </div>
  )

  const today = new Date().toISOString().slice(0, 10)
  const todayPresent = attendance.filter(a => a.marked_at?.startsWith(today) && a.status === 'present')
  const todayBlocked = attendance.filter(a => a.marked_at?.startsWith(today) && a.status !== 'present' && a.status !== 'override')
  const todayOverrides = attendance.filter(a => a.marked_at?.startsWith(today) && a.status === 'override')
  const isTrial = org.subscription_status === 'trial'
  const trialEnd = org.trial_ends_at ? new Date(org.trial_ends_at) : null
  const trialDaysLeft = trialEnd ? Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24)) : null
  const trialExpired = isTrial && trialDaysLeft !== null && trialDaysLeft <= 0
  const isExpired = org.subscription_status === 'expired' || org.subscription_status === 'read_only' || trialExpired
  const semesterEnd = org.semester_end ? new Date(org.semester_end) : null
  const daysLeft = semesterEnd ? Math.ceil((semesterEnd - new Date()) / (1000 * 60 * 60 * 24)) : null

  // Plan limits
  const planLimits = PLANS.find(p => p.key === org.plan)?.limits || {}
  const sessionLimitReached = planLimits.sessions && sessions.length >= planLimits.sessions
  const memberLimitReached = planLimits.members && members.length >= planLimits.members
  const lecturerLimitReached = planLimits.lecturers && invites.length >= planLimits.lecturers

  // ── Analytics data ─────────────────────────────────────────────
  const sessionStats = sessions.map(s => ({
    label: s.name.length > 10 ? s.name.slice(0, 10) + '…' : s.name,
    fullName: s.name,
    value: attendance.filter(a => a.session_id === s.id && a.status === 'present').length,
    rate: members.length > 0 ? Math.round((attendance.filter(a => a.session_id === s.id && a.status === 'present').length / members.length) * 100) : 0,
  }))

  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i))
    const key = d.toISOString().slice(0, 10)
    return { label: d.toLocaleDateString('en-GB', { weekday: 'short' }), value: attendance.filter(a => a.marked_at?.startsWith(key) && a.status === 'present').length }
  })

  // ── Member scorecard ────────────────────────────────────────────
  const memberScorecard = members.map(m => {
    const present = attendance.filter(a => a.member_id === m.id && a.status === 'present').length
    const total = sessions.length
    const rate = total > 0 ? Math.round((present / total) * 100) : 0
    return { ...m, presentCount: present, rate }
  }).sort((a, b) => a.rate - b.rate)

  // ── At-risk members (Institution) ───────────────────────────────
  const atRiskMembers = features.atRisk ? memberScorecard.filter(m => m.rate < 50 && sessions.length >= 3).slice(0, 8) : []

  // ── Quick stats (Pro+) ─────────────────────────────────────────
  const topAbsentee = memberScorecard[0]
  const bestSession = sessionStats.sort((a, b) => b.value - a.value)[0]
  const overallRate = members.length > 0 && sessions.length > 0
    ? Math.round((attendance.filter(a => a.status === 'present').length / (members.length * sessions.length)) * 100)
    : 0

  async function handleSignOut() { await signOut(); navigate('/') }

  async function saveSettings() {
    setSettingsSaving(true); setSettingsError(''); setSettingsSaved(false)
    const updates = {
      portal_banner: settingsForm.portal_banner.trim(),
      announce_text: settingsForm.announce_text.trim(),
      announce_active: settingsForm.announce_active,
      admin_display_name: settingsForm.admin_display_name.trim(),
    }
    if (features.vanitySlug) {
      const slug = settingsForm.vanity_slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')
      updates.vanity_slug = slug || null
    }
    const { error } = await supabase.from('organizations').update(updates).eq('id', org.id)
    if (error) {
      setSettingsError(error.code === '23505' ? 'That vanity slug is already taken. Choose a different one.' : error.message)
    } else {
      setSettingsSaved(true)
      await refreshOrg()
      setTimeout(() => setSettingsSaved(false), 3000)
    }
    setSettingsSaving(false)
  }


  // ── Plan themes ─────────────────────────────────────────────────
  const planTheme = {
    starter: {
      vars: '',
      navBg: '#ffffff', navBorder: '#e5e7eb', logoAccent: 'var(--blue)',
      planBadge: { background: '#e8f0fe', color: '#1e40af', border: '1px solid #bfdbfe' },
      planLabel: '⚡ Starter',
    },
    pro: {
      vars: '--blue:#7c3aed;--blue-dark:#5b21b6;--blue-light:#ede9fe;--blue-mid:#8b5cf6;--border-focus:#7c3aed;',
      navBg: 'linear-gradient(135deg,#1e1040,#2d1b69)',
      navBorder: 'transparent', logoAccent: '#a78bfa',
      planBadge: { background: 'rgba(167,139,250,0.15)', color: '#c4b5fd', border: '1px solid rgba(167,139,250,0.3)' },
      planLabel: '✦ Pro',
    },
    institution: {
      vars: '--blue:#b45309;--blue-dark:#92400e;--blue-light:#fef3c7;--blue-mid:#d97706;--border-focus:#b45309;--surface:#fafaf8;',
      navBg: 'linear-gradient(135deg,#0f1623,#1a2540)',
      navBorder: 'transparent', logoAccent: '#f59e0b',
      planBadge: { background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)' },
      planLabel: '👑 Institution',
    },
  }
  const theme = planTheme[org.plan] || planTheme.starter
  const isPremium = org.plan === 'pro' || org.plan === 'institution'

  const checklist = [
    { key: 'location', label: `Add a ${L.location?.toLowerCase()}`, done: locations.length > 0, tab: 'locations' },
    { key: 'session', label: `Add a ${L.session?.toLowerCase()}`, done: sessions.length > 0, tab: 'sessions' },
    { key: 'members', label: `Add ${L.members?.toLowerCase()}`, done: members.length > 0, tab: 'members' },
  ]
  const checklistDone = checklist.every(c => c.done)

  const TABS = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'members', label: L.members },
    { key: 'import-members', label: '↑ Import' },
    { key: 'sessions', label: L.sessions },
    { key: 'import-schedule', label: '↑ Schedule', comingSoon: !features.scheduleImport },
    { key: 'locations', label: L.locations },
    { key: 'attendance', label: L.attendance },
    ...(features.analytics ? [{ key: 'analytics', label: '📊 Analytics' }] : []),
    { key: 'lecturers', label: 'Lecturers' },
    { key: 'export', label: 'Export' },
    { key: 'settings', label: 'Settings' },
  ]

  return (
    <div className="page">
      {theme.vars && <style>{`:root{${theme.vars}}`}</style>}
      <nav className="topnav" style={{ background: theme.navBg, borderBottom: `1px solid ${theme.navBorder}`, ...(isPremium ? { boxShadow: '0 2px 20px rgba(0,0,0,0.25)' } : {}) }}>
        <div className="flex items-center gap-2">
          <div className="logo" style={isPremium ? { color: '#fff' } : {}}>
            Campus<span style={{ color: theme.logoAccent }}>Flow</span>
          </div>
          <span style={{ fontSize: 12, borderLeft: `1px solid ${isPremium ? 'rgba(255,255,255,0.15)' : 'var(--border)'}`, paddingLeft: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isPremium ? 'rgba(255,255,255,0.6)' : 'var(--text-muted)' }}>
            {org.name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isTrial && !trialExpired && trialDaysLeft !== null && (
            <span className="badge badge-amber">Trial: {trialDaysLeft}d left</span>
          )}
          {daysLeft !== null && daysLeft <= 14 && daysLeft > 0 && !isTrial && <span className="badge badge-amber">{daysLeft}d left</span>}
          {isExpired && <span className="badge badge-red">{trialExpired ? 'Trial ended' : 'Expired'}</span>}
          {org.plan !== 'institution' && (
            <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#fff', border: 'none', fontSize: 12, padding: '5px 12px' }} onClick={() => setUpgradeModal(true)}>
              Upgrade ↑
            </button>
          )}
          <span className="badge" style={theme.planBadge}>{theme.planLabel}</span>
          <button className="btn btn-ghost btn-sm" style={isPremium ? { color: 'rgba(255,255,255,0.7)' } : {}} onClick={handleSignOut}>Sign out</button>
        </div>
      </nav>

      {isExpired && (
        <div className="alert alert-error" style={{ borderRadius: 0, border: 'none', borderBottom: '1px solid var(--red-bg)', textAlign: 'center' }}>
          {trialExpired
            ? <>Your 14-day free trial has ended. Your data is safe — <span style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }} onClick={() => setUpgradeModal(true)}>activate your plan to continue →</span></>
            : <>Subscription expired — read-only mode. <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setUpgradeModal(true)}>Renew now →</span></>
          }
        </div>
      )}
      {isTrial && !trialExpired && trialDaysLeft !== null && trialDaysLeft <= 5 && (
        <div className="alert alert-warning" style={{ borderRadius: 0, border: 'none', borderBottom: '1px solid #fde68a', textAlign: 'center' }}>
          ⏳ Trial ends in <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}</strong> — <span style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }} onClick={() => setUpgradeModal(true)}>activate your plan now to avoid interruption →</span>
        </div>
      )}

      <div className="container" style={{ paddingTop: '1.5rem', paddingBottom: '3rem' }}>

        {!checklistDone && (
          <div className="card mb-4">
            <div className="flex justify-between items-center mb-2">
              <div>
                <div className="card-title">Get started checklist</div>
                <div className="card-sub">Complete these steps to activate attendance</div>
              </div>
              <span className="badge badge-blue">{checklist.filter(c => c.done).length}/{checklist.length}</span>
            </div>
            <ul className="checklist">
              {checklist.map(c => (
                <li key={c.key} onClick={() => setTab(c.tab)}>
                  <div className={`check-circle ${c.done ? 'done' : 'active'}`}>{c.done ? '✓' : ''}</div>
                  <span style={{ textDecoration: c.done ? 'line-through' : 'none', color: c.done ? 'var(--text-muted)' : 'var(--text)' }}>{c.label}</span>
                  {!c.done && <span style={{ marginLeft: 'auto', color: 'var(--blue)', fontSize: 13 }}>→</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="tab-bar">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`tab-btn ${tab === t.key ? 'active' : ''} ${t.comingSoon ? 'tab-btn-disabled' : ''}`}
              onClick={() => !t.comingSoon && setTab(t.key)}
              title={t.comingSoon ? 'Coming soon' : ''}
              style={t.comingSoon ? { opacity: 0.5, cursor: 'not-allowed', position: 'relative' } : {}}
            >
              {t.label}
              {t.comingSoon && <span style={{ fontSize: 9, background: '#f59e0b', color: '#fff', borderRadius: 99, padding: '1px 5px', marginLeft: 4, verticalAlign: 'middle', fontWeight: 700 }}>SOON</span>}
            </button>
          ))}
        </div>

        {/* ── Dashboard ── */}
        {tab === 'dashboard' && (
          <div className="fade-up">
            <div className="metric-grid">
              <div className="metric">
                <div className="flex items-center gap-1 mb-1"><span className="live-dot" /><span className="text-xs text-muted">LIVE</span></div>
                <div className="metric-val">{todayPresent.length + liveCount}</div>
                <div className="metric-lbl">Present today</div>
              </div>
              <div className="metric"><div className="metric-val">{members.length}</div><div className="metric-lbl">Total {L.members?.toLowerCase()}</div></div>
              <div className="metric"><div className="metric-val">{sessions.length}</div><div className="metric-lbl">{L.sessions}</div></div>
              <div className="metric"><div className="metric-val">{todayBlocked.length}</div><div className="metric-lbl">Blocked today</div></div>
              {todayOverrides.length > 0 && <div className="metric"><div className="metric-val" style={{ color: 'var(--amber)' }}>{todayOverrides.length}</div><div className="metric-lbl">Overrides</div></div>}
            </div>

            {/* ── Quick stats (Pro+) ── */}
            {features.analytics && sessions.length > 0 && members.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 10, marginBottom: '1rem' }}>
                <div className="card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--blue)' }}>{overallRate}%</div>
                  <div className="text-sm text-muted">Overall attendance rate</div>
                </div>
                {bestSession && (
                  <div className="card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>🏆 Best session</div>
                    <div style={{ fontWeight: 600, color: 'var(--green)' }}>{bestSession.fullName}</div>
                    <div className="text-sm text-muted">{bestSession.value} present</div>
                  </div>
                )}
                {topAbsentee && sessions.length >= 2 && (
                  <div className="card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>⚠️ Most absent</div>
                    <div style={{ fontWeight: 600, color: 'var(--red)' }}>{topAbsentee.name}</div>
                    <div className="text-sm text-muted">{topAbsentee.rate}% rate</div>
                  </div>
                )}
              </div>
            )}

            {/* ── At-risk members (Institution) ── */}
            {features.atRisk && atRiskMembers.length > 0 && (
              <div className="card mb-3" style={{ borderLeft: '3px solid var(--red)' }}>
                <div className="card-title mb-1">🚨 At-risk {L.members?.toLowerCase()} ({atRiskMembers.length})</div>
                <div className="card-sub mb-3">Below 50% attendance across all sessions</div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Name</th><th>{L.idField}</th><th>{L.group}</th><th>Rate</th></tr></thead>
                    <tbody>
                      {atRiskMembers.map(m => (
                        <tr key={m.id}>
                          <td style={{ fontWeight: 500 }}>{m.name}</td>
                          <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{m.member_id}</td>
                          <td>{m.group_name || '—'}</td>
                          <td><span className="badge badge-red">{m.rate}%</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="card mb-3">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="card-title">Today's {L.attendance?.toLowerCase()}</div>
                  <div className="card-sub">{new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setTab('attendance')}>View all</button>
              </div>
              {todayPresent.length === 0 ? (
                <div className="empty-state"><div className="empty-state-title">No check-ins yet today</div><div className="empty-state-sub">Updates live as members check in</div></div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>{L.member}</th><th>{L.idField}</th><th>{L.session}</th><th>Time</th></tr></thead>
                    <tbody>
                      {todayPresent.slice(0, 15).map(a => (
                        <tr key={a.id}>
                          <td style={{ fontWeight: 500 }}>{a.members?.name || a.member_name}</td>
                          <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{a.member_uid || '—'}</td>
                          <td>{a.sessions?.name || a.session_name}</td>
                          <td>{new Date(a.marked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {todayBlocked.length > 0 && (
              <div className="card">
                <div className="card-title mb-3">Blocked attempts today</div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>{L.member}</th><th>Reason</th><th>Time</th></tr></thead>
                    <tbody>
                      {todayBlocked.slice(0, 10).map(a => (
                        <tr key={a.id}>
                          <td>{a.member_name || '—'}</td>
                          <td><span className="badge badge-red">{a.status?.replace(/-/g, ' ')}</span></td>
                          <td>{new Date(a.marked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Members ── */}
        {tab === 'members' && (
          <div className="fade-up">
            <div className="flex justify-between items-center mb-3">
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 600 }}>{L.members}</h2>
                <p className="text-sm text-muted">{members.length} total{planLimits.members ? ` · limit: ${planLimits.members}` : ''}</p>
              </div>
              <div className="flex gap-2">
                {!isExpired && !memberLimitReached && (
                  <button className="btn btn-primary btn-sm" onClick={() => setMemberModal('add')}>+ Add {L.member?.toLowerCase()}</button>
                )}
                {memberLimitReached && (
                  <button className="btn btn-sm" style={{ background: '#f59e0b', color: '#fff', border: 'none' }} onClick={() => setUpgradeModal(true)}>
                    Limit reached — Upgrade ↑
                  </button>
                )}
              </div>
            </div>
            {members.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-state-icon">👥</div><div className="empty-state-title">No {L.members?.toLowerCase()} yet</div><div className="empty-state-sub">Add manually or use the Import tab</div><button className="btn btn-primary btn-sm mt-3" onClick={() => setMemberModal('add')}>+ Add first {L.member?.toLowerCase()}</button></div></div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th><th>{L.idField}</th><th>{L.group}</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => (
                      <tr key={m.id}>
                        <td style={{ fontWeight: 500 }}>{m.name}</td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{m.member_id}</td>
                        <td>{m.group_name || '—'}</td>
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => setMemberModal(m)}>Edit</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={async () => { if (window.confirm('Delete?')) { await deleteMember(m.id); loadAll() } }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button className="btn btn-secondary btn-sm mt-3" onClick={() => exportMembers(members, org)}>Download list (.xlsx)</button>
            <MemberModal isOpen={!!memberModal} member={memberModal === 'add' ? null : memberModal} org={org} L={L} onClose={() => setMemberModal(null)} onSave={loadAll} />
          </div>
        )}

        {tab === 'import-members' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: '0.25rem' }}>Import {L.members}</h2>
            <p className="text-sm text-muted mb-3">Upload Excel or CSV — duplicates are automatically skipped</p>
            <div className="card"><MemberUpload org={org} L={L} onDone={() => { loadAll(); setTab('members') }} /></div>
          </div>
        )}

        {/* ── Sessions ── */}
        {tab === 'sessions' && (
          <div className="fade-up">
            <div className="flex justify-between items-center mb-3">
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 600 }}>{L.sessions}</h2>
                <p className="text-sm text-muted">Attendance only opens during active {L.sessions?.toLowerCase()}{planLimits.sessions ? ` · ${sessions.length}/${planLimits.sessions} used` : ''}</p>
              </div>
              <div className="flex gap-2">
                {!isExpired && !sessionLimitReached && (
                  <button className="btn btn-primary btn-sm" onClick={() => setSessionModal('add')}>+ Add {L.session?.toLowerCase()}</button>
                )}
                {sessionLimitReached && (
                  <button className="btn btn-sm" style={{ background: '#f59e0b', color: '#fff', border: 'none' }} onClick={() => setUpgradeModal(true)}>
                    Limit reached — Upgrade ↑
                  </button>
                )}
              </div>
            </div>
            {sessions.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-state-icon">🗓️</div><div className="empty-state-title">No {L.sessions?.toLowerCase()} yet</div><button className="btn btn-primary btn-sm mt-3" onClick={() => setSessionModal('add')}>+ Add first {L.session?.toLowerCase()}</button></div></div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {sessions.map(s => (
                  <div key={s.id} className="card">
                    <div className="flex justify-between items-start">
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{s.name}</div>
                        <div className="text-sm text-muted">{s.group_name && `${s.group_name} · `}{s.start_time}–{s.end_time}</div>
                        {s.locations && <div className="text-xs text-muted mt-1">📍 {s.locations.name} ({s.locations.radius_metres}m)</div>}
                        {!s.location_id && <div className="text-xs mt-1" style={{ color: 'var(--amber)' }}>⚠️ No location — GPS check disabled</div>}
                        <div style={{ marginTop: 6, display: 'flex', gap: 4 }}>{(s.days || []).map(d => <span key={d} className="badge badge-blue">{d}</span>)}</div>
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-secondary btn-sm" onClick={() => setSessionModal(s)}>Edit</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={async () => { if (window.confirm('Delete?')) { await deleteSession(s.id); loadAll() } }}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <SessionModal isOpen={!!sessionModal} session={sessionModal === 'add' ? null : sessionModal} org={org} L={L} locations={locations} onClose={() => setSessionModal(null)} onSave={loadAll} />
          </div>
        )}

        {tab === 'import-schedule' && (
          <div className="fade-up">
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🗓️</div>
              <div className="card-title mb-2">Schedule import — coming soon</div>
              <div className="card-sub">Upload your full timetable as Excel or CSV and sessions will be created automatically. For now, add sessions manually from the Sessions tab.</div>
            </div>
          </div>
        )}

        {/* ── Locations ── */}
        {tab === 'locations' && (
          <div className="fade-up">
            <div className="flex justify-between items-center mb-3">
              <div><h2 style={{ fontSize: 18, fontWeight: 600 }}>{L.locations}</h2><p className="text-sm text-muted">GPS coordinates and radius per venue</p></div>
              {!isExpired && <button className="btn btn-primary btn-sm" onClick={() => setLocationModal('add')}>+ Add {L.location?.toLowerCase()}</button>}
            </div>
            {locations.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-state-icon">📍</div><div className="empty-state-title">No {L.locations?.toLowerCase()} yet</div><div className="empty-state-sub">Add at least one before creating sessions</div><button className="btn btn-primary btn-sm mt-3" onClick={() => setLocationModal('add')}>+ Add first {L.location?.toLowerCase()}</button></div></div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {locations.map(loc => (
                  <div key={loc.id} className="card">
                    <div className="flex justify-between items-center">
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{loc.name}</div>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--mono)' }}>Lat: {loc.lat?.toFixed(6)} · Lng: {loc.lng?.toFixed(6)}</div>
                        <div className="text-xs text-muted mt-1">Radius: {loc.radius_metres}m · {loc.calibrated_at ? `Calibrated: ${new Date(loc.calibrated_at).toLocaleDateString()}` : 'Not calibrated'}</div>
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-secondary btn-sm" onClick={() => setLocationModal(loc)}>Edit</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={async () => { if (window.confirm('Delete?')) { await deleteLocation(loc.id); loadAll() } }}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <LocationModal isOpen={!!locationModal} location={locationModal === 'add' ? null : locationModal} org={org} L={L} user={user} onClose={() => setLocationModal(null)} onSave={loadAll} />
          </div>
        )}

        {/* ── Attendance ── */}
        {tab === 'attendance' && (
          <div className="fade-up">
            <div className="flex justify-between items-center mb-3">
              <div><h2 style={{ fontSize: 18, fontWeight: 600 }}>{L.attendance} log</h2><p className="text-sm text-muted">Full history including blocked attempts and overrides</p></div>
              {!isExpired && <button className="btn btn-primary btn-sm" onClick={() => setOverrideModal({})}>+ Manual override</button>}
            </div>
            {attendance.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-state-title">No records yet</div></div></div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Name</th><th>{L.idField}</th><th>{L.session}</th><th>Date</th><th>Time</th><th>GPS</th><th>Status</th></tr></thead>
                  <tbody>
                    {attendance.slice(0, 100).map(a => (
                      <tr key={a.id}>
                        <td style={{ fontWeight: 500 }}>{a.members?.name || a.member_name || '—'}</td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{a.member_uid || '—'}</td>
                        <td>{a.sessions?.name || a.session_name || '—'}</td>
                        <td>{new Date(a.marked_at).toLocaleDateString()}</td>
                        <td>{new Date(a.marked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                        <td><span className={`badge ${a.gps_verified ? 'badge-green' : 'badge-gray'}`}>{a.gps_verified ? 'OK' : '—'}</span></td>
                        <td>
                          <span className={`badge ${a.status === 'present' ? 'badge-green' : a.status === 'override' ? 'badge-amber' : 'badge-red'}`}>{a.status}</span>
                          {a.override_reason && <span className="text-xs text-muted" style={{ marginLeft: 4 }} title={a.override_reason}>ℹ️</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <OverrideModal isOpen={!!overrideModal} members={members} sessions={sessions} org={org} L={L} user={user} onClose={() => setOverrideModal(null)} onSave={loadAll} />
          </div>
        )}

        {/* ── Analytics (Pro+) ── */}
        {features.analytics && tab === 'analytics' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: '0.25rem' }}>Analytics</h2>
            <p className="text-sm text-muted mb-4">Attendance patterns across all your {L.sessions?.toLowerCase()}</p>

            {sessions.length === 0 || members.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-state-title">No data yet</div><div className="empty-state-sub">Add sessions and members, then check-ins will appear here</div></div></div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="card">
                    <div className="card-title mb-1">Check-ins last 7 days</div>
                    <div className="card-sub mb-3">Daily present count</div>
                    <BarChart data={last7Days} color="var(--blue)" />
                  </div>
                  <div className="card">
                    <div className="card-title mb-1">Attendance per {L.session?.toLowerCase()}</div>
                    <div className="card-sub mb-3">Total present records</div>
                    <BarChart data={sessionStats.map(s => ({ label: s.label, value: s.value }))} color="var(--green)" />
                  </div>
                </div>

                {/* Member scorecard */}
                {features.scorecard && (
                  <div className="card">
                    <div className="card-title mb-1">{L.member} scorecard</div>
                    <div className="card-sub mb-3">Sorted by attendance rate (lowest first)</div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>Name</th><th>{L.idField}</th><th>{L.group}</th><th>Present</th><th>Rate</th></tr></thead>
                        <tbody>
                          {memberScorecard.slice(0, 30).map(m => (
                            <tr key={m.id}>
                              <td style={{ fontWeight: 500 }}>{m.name}</td>
                              <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{m.member_id}</td>
                              <td>{m.group_name || '—'}</td>
                              <td>{m.presentCount}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ flex: 1, height: 6, background: 'var(--gray-100)', borderRadius: 99, overflow: 'hidden', minWidth: 60 }}>
                                    <div style={{ height: '100%', width: `${m.rate}%`, background: m.rate >= 75 ? 'var(--green)' : m.rate >= 50 ? 'var(--amber)' : 'var(--red)', borderRadius: 99 }} />
                                  </div>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: m.rate >= 75 ? 'var(--green)' : m.rate >= 50 ? 'var(--amber)' : 'var(--red)', minWidth: 34 }}>{m.rate}%</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Lecturers ── */}
        {tab === 'lecturers' && (
          <div className="fade-up">
            <div className="flex justify-between items-center mb-3">
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 600 }}>Lecturers</h2>
                <p className="text-sm text-muted">View-only access to their assigned sessions{planLimits.lecturers ? ` · ${invites.length}/${planLimits.lecturers} used` : ''}</p>
              </div>
              {!isExpired && !lecturerLimitReached && (
                <div className="flex gap-2">
                  <button className="btn btn-secondary btn-sm" onClick={loadAll}>↻ Refresh</button>
                  <button className="btn btn-primary btn-sm" onClick={() => setInviteShareModal('new')}>+ Invite lecturer</button>
                </div>
              )}
              {lecturerLimitReached && (
                <button className="btn btn-sm" style={{ background: '#f59e0b', color: '#fff', border: 'none' }} onClick={() => setUpgradeModal(true)}>Limit reached — Upgrade ↑</button>
              )}
            </div>
            <div className="alert alert-info mb-3" style={{ fontSize: 13 }}>
              📎 Invites work via a share link — send it over WhatsApp, email, SMS, or any platform. No email server needed.
            </div>
            {invites.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-state-icon">👨‍🏫</div><div className="empty-state-title">No lecturers invited yet</div><div className="empty-state-sub">Generate a link and share it any way you like</div></div></div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {invites.map(inv => (
                  <div key={inv.id} className="card">
                    <div className="flex justify-between items-center">
                      <div>
                        <div style={{ fontWeight: 500 }}>{inv.email}</div>
                        <div className="text-xs text-muted mt-1">
                          Invited {new Date(inv.created_at).toLocaleDateString()} ·
                          {inv.accepted ? <span style={{ color: 'var(--green)', marginLeft: 4 }}>✓ Accepted</span>
                            : new Date(inv.expires_at) < new Date() ? <span style={{ color: 'var(--red)', marginLeft: 4 }}>Expired</span>
                              : <span style={{ color: 'var(--amber)', marginLeft: 4 }}>Pending</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!inv.accepted && (
                          <button className="btn btn-secondary btn-sm" onClick={() => setInviteShareModal(inv)}>Share link</button>
                        )}
                        {inv.accepted && inv.lecturer_id && (
                          <>
                            <AssignSessionsButton lecturerId={inv.lecturer_id} sessions={sessions} org={org} L={L} onSave={loadAll} />
                          </>
                        )}
                        {inv.accepted && !inv.lecturer_id && (
                          <span className="badge badge-amber" title="Accepted but user ID not linked yet — click Refresh">⚠ Needs refresh</span>
                        )}
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={async () => { if (window.confirm('Revoke this invite?')) { await revokeInvite(inv.id); loadAll() } }}>Revoke</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <InviteModal isOpen={inviteShareModal === 'new'} org={org} onClose={() => setInviteShareModal(null)} onSave={inv => { setInviteShareModal(inv); loadAll() }} />
            {inviteShareModal && inviteShareModal !== 'new' && (
              <ShareLinkModal invite={inviteShareModal} onClose={() => setInviteShareModal(null)} />
            )}
          </div>
        )}

        {/* ── Export ── */}
        {tab === 'export' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: '0.25rem' }}>Export data</h2>
            <p className="text-sm text-muted mb-3">Download your {L.attendance?.toLowerCase()} records</p>
            <div className="card">
              <div className="form-group">
                <label className="field-label">{L.session}</label>
                <select value={exportFilters.session} onChange={e => setExportFilters({ ...exportFilters, session: e.target.value })}>
                  <option value="">All {L.sessions?.toLowerCase()}</option>
                  {sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="grid-2">
                <div className="form-group"><label className="field-label">From date</label><input type="date" value={exportFilters.dateFrom} onChange={e => setExportFilters({ ...exportFilters, dateFrom: e.target.value })} /></div>
                <div className="form-group"><label className="field-label">To date</label><input type="date" value={exportFilters.dateTo} onChange={e => setExportFilters({ ...exportFilters, dateTo: e.target.value })} /></div>
              </div>
              <div className="form-group">
                <label className="field-label">Format</label>
                <select value={exportFilters.format} onChange={e => setExportFilters({ ...exportFilters, format: e.target.value })}>
                  <option value="xlsx">Excel (.xlsx)</option>
                  <option value="csv">CSV (.csv)</option>
                </select>
              </div>
              <button className="btn btn-primary mt-2" onClick={() => {
                const filtered = attendance.filter(a => {
                  if (exportFilters.session && a.session_id !== exportFilters.session) return false
                  if (exportFilters.dateFrom && a.marked_at < exportFilters.dateFrom + 'T00:00:00') return false
                  if (exportFilters.dateTo && a.marked_at > exportFilters.dateTo + 'T23:59:59') return false
                  return true
                })
                exportAttendance(filtered, org, exportFilters.format, `campusflow-${org.name.replace(/\s+/g, '-').toLowerCase()}`)
              }}>Download {exportFilters.format.toUpperCase()}</button>
            </div>
          </div>
        )}


        {/* ── Settings ── */}
        {tab === 'settings' && (
          <div className="fade-up">
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: '0.25rem' }}>Settings</h2>
            <p className="text-sm text-muted mb-3">Customise your portal and sharing options</p>

            {/* Portal banner & display name */}
            <div className="card mb-3">
              <div className="card-title mb-3">Portal appearance</div>
              <div className="form-group">
                <label className="field-label">Portal banner title</label>
                <input type="text" placeholder={org.name} value={settingsForm.portal_banner} onChange={e => setSettingsForm(f => ({ ...f, portal_banner: e.target.value }))} />
                <div className="field-hint">This large title is shown to members when they open your attend portal. Leave blank to use your org name.</div>
              </div>
              <div className="form-group">
                <label className="field-label">Admin display name</label>
                <input type="text" placeholder="e.g. Dr. Kwame Mensah" value={settingsForm.admin_display_name} onChange={e => setSettingsForm(f => ({ ...f, admin_display_name: e.target.value }))} />
                <div className="field-hint">Shown as "Managed by [name]" on the attend portal.</div>
              </div>
            </div>

            {/* Announcement board */}
            <div className="card mb-3">
              <div className="card-title mb-1">Announcement board</div>
              <div className="card-sub mb-3">Members see this notice every time they open the attend portal</div>
              <div className="form-group">
                <label className="field-label">Announcement message</label>
                <textarea placeholder="e.g. Exam is next week. All lectures are mandatory." value={settingsForm.announce_text} onChange={e => setSettingsForm(f => ({ ...f, announce_text: e.target.value }))} style={{ minHeight: 80 }} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                <input type="checkbox" checked={settingsForm.announce_active} onChange={e => setSettingsForm(f => ({ ...f, announce_active: e.target.checked }))} />
                Show announcement to members (toggle off to hide without deleting)
              </label>
            </div>

            {/* Vanity slug (Institution only) */}
            {features.vanitySlug && (
              <div className="card mb-3">
                <div className="card-title mb-1">Custom URL slug</div>
                <div className="card-sub mb-3">Members can reach your portal at /attend/your-slug instead of the 8-char code</div>
                <div className="form-group">
                  <label className="field-label">Your slug</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{window.location.origin}/attend/</span>
                    <input type="text" placeholder="ktu-csf" value={settingsForm.vanity_slug} onChange={e => setSettingsForm(f => ({ ...f, vanity_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} />
                  </div>
                  <div className="field-hint">Lowercase letters, numbers, and hyphens only. Must be unique.</div>
                </div>
              </div>
            )}

            {/* Save button */}
            {settingsError && <div className="alert alert-error mb-3">{settingsError}</div>}
            <div className="flex gap-2 mb-4">
              <button className="btn btn-primary" disabled={settingsSaving} onClick={saveSettings}>
                {settingsSaving ? <><span className="spinner" /> Saving...</> : 'Save settings'}
              </button>
              {settingsSaved && <span className="alert alert-success" style={{ padding: '6px 14px', margin: 0 }}>✓ Saved</span>}
            </div>

            {/* Org info (read-only) */}
            <div className="card mb-3">
              <div className="card-title mb-3">Organisation details</div>
              {[
                { label: 'Name', val: org.name },
                { label: 'Type', val: org.org_type },
                { label: 'Plan', val: org.plan },
                { label: 'Semester start', val: org.semester_start || '—' },
                { label: 'Semester end', val: org.semester_end || '—' },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span className="text-muted">{r.label}</span>
                  <span style={{ fontWeight: 500 }}>{r.val}</span>
                </div>
              ))}
            </div>

            {/* Short code */}
            <div className="card mb-3">
              <div className="card-title mb-2">Organisation code</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 28, fontWeight: 700, color: 'var(--blue)', letterSpacing: 4, marginBottom: 8 }}>{org.short_code}</div>
              <p className="text-sm text-muted mb-2">Share with {L.members?.toLowerCase()} so they can mark {L.attendance?.toLowerCase()}.</p>
              <div className="flex gap-2">
                <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(org.short_code); alert('Code copied!') }}>Copy code</button>
                <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/attend`); alert('Link copied!') }}>Copy attend link</button>
                <a href={`https://wa.me/?text=${encodeURIComponent(`Join my organisation on CampusFlow!\n\n1. Go to: ${window.location.origin}/attend\n2. Enter code: ${org.short_code}\n\nSee you there!`)}`} target="_blank" rel="noopener noreferrer" className="btn btn-success btn-sm" style={{ textDecoration: 'none' }}>Share on WhatsApp</a>
              </div>
            </div>

            {/* Upgrade prompt */}
            {org.plan !== 'institution' && (
              <div className="card" style={{ background: 'linear-gradient(135deg,rgba(245,158,11,0.05),rgba(245,158,11,0.02))', borderColor: 'rgba(245,158,11,0.3)' }}>
                <div className="card-title mb-1">Upgrade your plan</div>
                <div className="card-sub mb-3">Get analytics, higher limits, and more features</div>
                <button className="btn btn-primary" style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', border: 'none' }} onClick={() => setUpgradeModal(true)}>
                  See upgrade options →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Upgrade Modal ── */}
      <UpgradeModal isOpen={upgradeModal} currentPlan={org.plan} org={org} onClose={() => setUpgradeModal(false)} onUpgraded={refreshOrg} />
    </div>
  )
}

// ── Member Modal ──────────────────────────────────────────────────
function MemberModal({ isOpen, member, org, L, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', member_id: '', group_name: '', email: '', phone: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (member) setForm({ name: member.name || '', member_id: member.member_id || '', group_name: member.group_name || '', email: member.email || '', phone: member.phone || '' })
    else setForm({ name: '', member_id: '', group_name: '', email: '', phone: '' })
    setError('')
  }, [member, isOpen])
  async function save() {
    if (!form.name || !form.member_id) { setError('Name and ID are required.'); return }
    setLoading(true)
    const { error: err } = member ? await updateMember(member.id, form) : await addMember({ ...form, org_id: org.id })
    if (err) { setError(err.message); setLoading(false); return }
    setLoading(false); onSave(); onClose()
  }
  if (!isOpen) return null
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{member ? 'Edit' : 'Add'} {L.member}</div>
        {error && <div className="alert alert-error mb-3">{error}</div>}
        <div className="form-group"><label className="field-label">Full name *</label><input type="text" autoFocus value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
        <div className="form-group">
          <label className="field-label">{L.idField} *</label>
          <input type="text" value={form.member_id} onChange={e => setForm({ ...form, member_id: e.target.value })} />
          <div className="field-hint">Must exactly match what the member types when checking in.</div>
        </div>
        <div className="form-group"><label className="field-label">{L.group} (optional)</label><input type="text" value={form.group_name} onChange={e => setForm({ ...form, group_name: e.target.value })} /></div>
        <div className="grid-2">
          <div className="form-group"><label className="field-label">Email (optional)</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div className="form-group"><label className="field-label">Phone (optional)</label><input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
        </div>
        <div className="flex gap-2 mt-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={loading} onClick={save}>{loading ? <span className="spinner" /> : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

// ── Session Modal ─────────────────────────────────────────────────
function SessionModal({ isOpen, session, org, L, locations, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', group_name: '', start_time: '', end_time: '', days: [], location_id: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (session) setForm({ name: session.name || '', group_name: session.group_name || '', start_time: session.start_time || '', end_time: session.end_time || '', days: session.days || [], location_id: session.location_id || '' })
    else setForm({ name: '', group_name: '', start_time: '', end_time: '', days: [], location_id: '' })
    setError('')
  }, [session, isOpen])
  function toggleDay(d) { setForm(f => ({ ...f, days: f.days.includes(d) ? f.days.filter(x => x !== d) : [...f.days, d] })) }
  async function save() {
    if (!form.name || !form.start_time || !form.end_time || !form.days.length) { setError('Name, time, and at least one day are required.'); return }
    setLoading(true)
    const { error: err } = session ? await updateSession(session.id, form) : await addSession({ ...form, org_id: org.id })
    if (err) { setError(err.message); setLoading(false); return }
    setLoading(false); onSave(); onClose()
  }
  if (!isOpen) return null
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{session ? 'Edit' : 'Add'} {L.session}</div>
        {error && <div className="alert alert-error mb-3">{error}</div>}
        <div className="form-group"><label className="field-label">{L.session} name *</label><input type="text" autoFocus value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
        <div className="form-group"><label className="field-label">{L.group} (optional)</label><input type="text" value={form.group_name} onChange={e => setForm({ ...form, group_name: e.target.value })} /></div>
        <div className="grid-2">
          <div className="form-group"><label className="field-label">Start time *</label><input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} /></div>
          <div className="form-group"><label className="field-label">End time *</label><input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} /></div>
        </div>
        <div className="form-group"><label className="field-label">Days *</label><div className="day-pills mt-1">{DAYS.map(d => <div key={d.key} className={`day-pill ${form.days.includes(d.key) ? 'selected' : ''}`} onClick={() => toggleDay(d.key)}>{d.label}</div>)}</div></div>
        <div className="form-group">
          <label className="field-label">{L.location}</label>
          <select value={form.location_id} onChange={e => setForm({ ...form, location_id: e.target.value })}>
            <option value="">No GPS lock</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          {!form.location_id && <div className="field-hint" style={{ color: 'var(--amber)' }}>⚠️ Without a location, members can check in from anywhere.</div>}
        </div>
        <div className="flex gap-2 mt-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={loading} onClick={save}>{loading ? <span className="spinner" /> : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

// ── Location Modal ────────────────────────────────────────────────
function LocationModal({ isOpen, location, org, L, user, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', lat: '', lng: '', radius_metres: 80 })
  const [gpsLoading, setGpsLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (location) setForm({ name: location.name || '', lat: location.lat || '', lng: location.lng || '', radius_metres: location.radius_metres || 80 })
    else setForm({ name: '', lat: '', lng: '', radius_metres: 80 })
    setError('')
  }, [location, isOpen])
  async function calibrate() {
    setGpsLoading(true)
    try {
      const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 12000 }))
      const lat = pos.coords.latitude.toFixed(6)
      const lng = pos.coords.longitude.toFixed(6)
      if (window.confirm(GPS_WARNING(lat, lng, form.radius_metres, form.name || 'this location'))) setForm(f => ({ ...f, lat, lng }))
    } catch { setError('Could not get your location. Make sure GPS is enabled.') }
    setGpsLoading(false)
  }
  async function save() {
    if (!form.name || !form.lat || !form.lng) { setError('Name and coordinates are required.'); return }
    setLoading(true)
    const data = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng), radius_metres: parseInt(form.radius_metres), org_id: org.id, calibrated_at: new Date().toISOString(), calibrated_by: user?.id }
    const { error: err } = location ? await updateLocation(location.id, data) : await addLocation(data)
    if (err) { setError(err.message); setLoading(false); return }
    setLoading(false); onSave(); onClose()
  }
  if (!isOpen) return null
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{location ? 'Edit' : 'Add'} {L.location}</div>
        <div className="modal-sub">Stand inside the venue and calibrate for best accuracy.</div>
        {error && <div className="alert alert-error mb-3">{error}</div>}
        <div className="form-group"><label className="field-label">{L.location} name *</label><input type="text" autoFocus value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
        <button className="btn btn-secondary mb-3" disabled={gpsLoading} onClick={calibrate}>
          {gpsLoading ? <><span className="spinner" /> Detecting...</> : '📍 Calibrate my current location'}
        </button>
        <div className="grid-2">
          <div className="form-group"><label className="field-label">Latitude *</label><input type="number" step="0.000001" placeholder="5.6502" value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })} /></div>
          <div className="form-group"><label className="field-label">Longitude *</label><input type="number" step="0.000001" placeholder="-0.1864" value={form.lng} onChange={e => setForm({ ...form, lng: e.target.value })} /></div>
        </div>
        <div className="form-group">
          <label className="field-label">Allowed radius: <strong>{form.radius_metres}m</strong></label>
          <input type="range" min="20" max="500" step="10" value={form.radius_metres} onChange={e => setForm({ ...form, radius_metres: e.target.value })} />
          <div className="field-hint">80–100m works for most lecture halls. Increase for outdoor venues.</div>
        </div>
        <div className="flex gap-2 mt-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={loading} onClick={save}>{loading ? <span className="spinner" /> : 'Save location'}</button>
        </div>
      </div>
    </div>
  )
}

// ── Override Modal ────────────────────────────────────────────────
function OverrideModal({ isOpen, members, sessions, org, L, user, onClose, onSave }) {
  const [form, setForm] = useState({ member_id: '', session_id: '', status: 'present', reason: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { setForm({ member_id: '', session_id: '', status: 'present', reason: '' }); setError('') }, [isOpen])
  async function save() {
    if (!form.member_id || !form.session_id || !form.reason.trim()) { setError('All fields including reason are required.'); return }
    setLoading(true)
    const member = members.find(m => m.id === form.member_id)
    const session = sessions.find(s => s.id === form.session_id)
    const { error: err } = await overrideAttendance({ org_id: org.id, member_id: form.member_id, member_name: member?.name, member_uid: member?.member_id, session_id: form.session_id, session_name: session?.name, device_token: 'admin-override', gps_verified: false, override_reason: form.reason, override_by: user?.id })
    if (err) { setError(err.message); setLoading(false); return }
    setLoading(false); onSave(); onClose()
  }
  if (!isOpen) return null
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">Manual override</div>
        <div className="modal-sub">Logged permanently. Visible to the assigned lecturer.</div>
        {error && <div className="alert alert-error mb-3">{error}</div>}
        <div className="alert alert-warning mb-3">⚠️ A reason is required and cannot be edited after saving.</div>
        <div className="form-group"><label className="field-label">{L.member} *</label><select value={form.member_id} onChange={e => setForm({ ...form, member_id: e.target.value })}><option value="">Select...</option>{members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.member_id})</option>)}</select></div>
        <div className="form-group"><label className="field-label">{L.session} *</label><select value={form.session_id} onChange={e => setForm({ ...form, session_id: e.target.value })}><option value="">Select...</option>{sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
        <div className="form-group"><label className="field-label">Mark as *</label><select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}><option value="present">Present</option><option value="absent">Absent</option></select></div>
        <div className="form-group"><label className="field-label">Reason *</label><textarea placeholder="e.g. Student was present but GPS failed due to poor signal." value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} /></div>
        <div className="flex gap-2 mt-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={loading || !form.reason.trim()} onClick={save}>{loading ? <span className="spinner" /> : 'Save override'}</button>
        </div>
      </div>
    </div>
  )
}

// ── Invite Modal (creates invite, then shows share panel) ─────────
function InviteModal({ isOpen, org, onClose, onSave }) {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { setEmail(''); setError('') }, [isOpen])
  async function send() {
    setLoading(true); setError('')
    const { data, error: err } = await inviteLecturer(org.id, email.trim().toLowerCase())
    if (err) { setError(err.code === '23505' ? 'This email has already been invited.' : err.message); setLoading(false); return }
    setLoading(false)
    onSave(data) // pass invite object to switch to share modal
  }
  if (!isOpen) return null
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">Invite a lecturer</div>
        <div className="modal-sub">Enter their email, then share the link however you like — WhatsApp, email, SMS.</div>
        {error && <div className="alert alert-error mb-3">{error}</div>}
        <div className="form-group"><label className="field-label">Lecturer email *</label><input type="email" autoFocus placeholder="lecturer@example.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} /></div>
        <div className="alert alert-info mb-3" style={{ fontSize: 12 }}>View-only access to sessions you assign. Cannot edit members, sessions, or settings.</div>
        <div className="flex gap-2 mt-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={loading || !email} onClick={send}>{loading ? <><span className="spinner" /> Creating...</> : 'Generate invite link →'}</button>
        </div>
      </div>
    </div>
  )
}

// ── Share Link Modal ──────────────────────────────────────────────
function ShareLinkModal({ invite, onClose }) {
  const [copied, setCopied] = useState(false)
  if (!invite) return null
  const link = `${window.location.origin}/invite/${invite.token}`
  const message = `Hi! You've been invited as a lecturer on CampusFlow.\n\nClick this link to set up your account (expires in 48 hours):\n${link}`

  function copy() {
    navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">Share invite link</div>
        <div className="modal-sub">Send this link to {invite.email} — expires in 48 hours</div>
        <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius)', padding: '12px 14px', fontFamily: 'var(--mono)', fontSize: 12, wordBreak: 'break-all', color: 'var(--blue)', marginBottom: '1rem', border: '1px solid var(--border)' }}>
          {link}
        </div>
        <div style={{ display: 'grid', gap: 10, marginBottom: '1rem' }}>
          <button className="btn btn-primary" onClick={copy}>{copied ? '✓ Copied!' : '📋 Copy link'}</button>
          <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener noreferrer" className="btn btn-success" style={{ textDecoration: 'none', textAlign: 'center' }}>
            💬 Share on WhatsApp
          </a>
          <a href={`mailto:${invite.email}?subject=${encodeURIComponent('You have been invited to CampusFlow')}&body=${encodeURIComponent(message)}`} className="btn btn-secondary" style={{ textDecoration: 'none', textAlign: 'center' }}>
            ✉️ Open in email client
          </a>
        </div>
        <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius)', padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)', marginBottom: '1rem' }}>
          <strong>Pre-written message:</strong><br />
          <span style={{ lineHeight: 1.7, display: 'block', marginTop: 4, whiteSpace: 'pre-wrap' }}>{message}</span>
          <button className="btn btn-ghost btn-sm mt-2" onClick={() => { navigator.clipboard.writeText(message); alert('Message copied!') }}>Copy full message</button>
        </div>
        <button className="btn btn-secondary btn-full" onClick={onClose}>Done</button>
      </div>
    </div>
  )
}

// ── Upgrade Modal ─────────────────────────────────────────────────
function UpgradeModal({ isOpen, currentPlan, org, onClose, onUpgraded }) {
  const [ref, setRef] = useState('')
  const [payDone, setPayDone] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [verified, setVerified] = useState(false)
  const [error, setError] = useState('')
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [livePrices, setLivePrices] = useState({})

  useEffect(() => {
    if (isOpen) {
      setRef(''); setPayDone(false); setVerifying(false); setVerified(false); setError(''); setSelectedPlan(null)
      getPlanPricing().then(({ data }) => { if (data) setLivePrices(Object.fromEntries(data.map(p => [p.plan_key, p.price]))) })
    }
  }, [isOpen])

  const upgradePlans = PLANS.filter(p => {
    if (currentPlan === 'starter') return p.key === 'pro' || p.key === 'institution'
    if (currentPlan === 'pro') return p.key === 'institution'
    return false
  })

  function payNow() {
    if (!selectedPlan) return
    const price = livePrices[selectedPlan.key] || selectedPlan.price
    const handler = window.PaystackPop.setup({
      key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
      email: org.admin_email || '',
      amount: price * 100,
      currency: 'GHS',
      ref: 'CF_UP_' + Date.now(),
      metadata: { plan: selectedPlan.key, org_id: org.id, upgrade: true },
      onSuccess: () => setTimeout(() => setPayDone(true), 500),
      onCancel: () => {},
    })
    handler.openIframe()
    setTimeout(() => setPayDone(true), 2000)
  }

  async function verifyAndUpgrade() {
    if (!ref.trim()) return
    setVerifying(true); setError('')
    try {
      // Check not already used
      const { data: existing } = await supabase.from('organizations').select('id').eq('paystack_ref', ref.trim()).maybeSingle()
      if (existing) { setError('This reference has already been used.'); setVerifying(false); return }

      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ reference: ref.trim() }),
      })
      const result = await res.json()
      if (!result.valid) { setError(result.message || 'Payment not verified.'); setVerifying(false); return }

      // Update plan
      await supabase.from('organizations').update({ plan: selectedPlan.key, paystack_ref: result.reference, subscription_status: 'active' }).eq('id', org.id)
      setVerified(true)
      await onUpgraded()
    } catch (e) { setError('Verification failed. Try again.') }
    setVerifying(false)
  }

  if (!isOpen) return null
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540 }}>
        {verified ? (
          <>
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <div style={{ fontSize: 48, marginBottom: '0.75rem' }}>🎉</div>
              <div className="modal-title">Upgraded successfully!</div>
              <div className="modal-sub mb-4">You're now on the {selectedPlan?.name} plan. Refresh to see your new features.</div>
              <button className="btn btn-primary btn-full" onClick={() => { onClose(); window.location.reload() }}>Refresh now →</button>
            </div>
          </>
        ) : !selectedPlan ? (
          <>
            <div className="modal-title mb-1">Upgrade your plan</div>
            <div className="modal-sub mb-4">Choose a plan to continue</div>
            <div style={{ display: 'grid', gap: 12 }}>
              {upgradePlans.map(plan => (
                <div key={plan.key} style={{ border: '2px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', cursor: 'pointer', transition: 'all 0.15s' }}
                  onClick={() => setSelectedPlan(plan)}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                  <div className="flex justify-between items-center mb-2">
                    <span style={{ fontWeight: 700, fontSize: 16 }}>{plan.name}</span>
                    <span style={{ fontWeight: 800, fontSize: 20 }}>GH₵{livePrices[plan.key] || plan.price}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}>/sem</span></span>
                  </div>
                  {plan.features.slice(0, 4).map((f, i) => <div key={i} style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 2 }}>✓ {f}</div>)}
                </div>
              ))}
            </div>
            <button className="btn btn-secondary btn-full mt-3" onClick={onClose}>Cancel</button>
          </>
        ) : !payDone ? (
          <>
            <div className="modal-title mb-1">Upgrade to {selectedPlan.name}</div>
            <div className="modal-sub mb-4">GH₵{livePrices[selectedPlan.key] || selectedPlan.price} / semester · pay via Paystack, then confirm your reference</div>
            <div className="alert alert-info mb-4">After paying, Paystack will show you a reference code. Copy it and paste it below.</div>
            <button className="btn btn-primary btn-full mb-3" onClick={payNow}>Pay GH₵{livePrices[selectedPlan.key] || selectedPlan.price} via Paystack →</button>
            <button className="btn btn-ghost btn-full" onClick={() => setSelectedPlan(null)}>← Back</button>
          </>
        ) : (
          <>
            <div className="modal-title mb-1">Paste your payment reference</div>
            <div className="modal-sub mb-4">Copy it exactly from the Paystack confirmation screen</div>
            {error && <div className="alert alert-error mb-3">{error}</div>}
            <div className="form-group">
              <label className="field-label">Payment reference *</label>
              <input type="text" autoFocus placeholder="e.g. CF_UP_1746789012345" value={ref} onChange={e => setRef(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-full mt-2" disabled={verifying || !ref.trim()} onClick={verifyAndUpgrade}>
              {verifying ? <><span className="spinner" /> Verifying...</> : 'Confirm & upgrade →'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ── Assign Sessions ───────────────────────────────────────────────
function AssignSessionsButton({ lecturerId, sessions, org, L, onSave }) {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState([])
  async function save() {
    for (const sid of selected) { await assignLecturerToSession(org.id, lecturerId, sid) }
    setOpen(false); onSave()
  }
  return (
    <>
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>Assign sessions</button>
      {open && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">Assign {L.sessions?.toLowerCase()}</div>
            <div className="modal-sub mb-3">Select which {L.sessions?.toLowerCase()} this lecturer can view.</div>
            <div style={{ display: 'grid', gap: 8, marginBottom: '1rem' }}>
              {sessions.map(s => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                  <input type="checkbox" checked={selected.includes(s.id)} onChange={e => setSelected(prev => e.target.checked ? [...prev, s.id] : prev.filter(x => x !== s.id))} />
                  <span>{s.name}</span>
                  {s.group_name && <span className="text-xs text-muted">{s.group_name}</span>}
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={save}>Save assignments</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
